import { FluentCustomizations } from '@uifabric/fluent-theme'
import { initializeIcons } from '@uifabric/icons'
import { Customizer } from '@uifabric/utilities/lib/customizations/Customizer'
import { t } from 'i18next'
import * as Immutable from 'immutable'
import { Fabric } from 'office-ui-fabric-react/lib/Fabric'
import * as React from 'react'
import { IOWidget, Updater, any, async, fromJSX } from 'widgets-for-react'
import { AsyncMonitor } from './async_monitor'
import { form_renderer } from './renderers'
import { status_thank_you_renderer } from './status_message_renderers'
import { DependenciesState, DependencyId, DependencyResult, FieldData, FieldId, FormState, IsMet } from './types'
import { get_dependency_result } from './utils'

initializeIcons()

const BaseForm: IOWidget<FormState, Updater<FormState>> = (s0) => {
  let dependencies_async_list = Immutable.List<{
    dep_res: { kind: 'async'; value: AsyncMonitor<IsMet> }
    dep_id: DependencyId
    field_id: FieldId
  }>()
  s0.dependencies_state.forEach((field_id__dep_res, dep_id) => {
    field_id__dep_res.forEach((dep_res, field_id) => {
      if (dep_res.kind == 'async') dependencies_async_list = dependencies_async_list.push({ dep_id, field_id, dep_res })
    })
  })
  return any<Updater<FormState>>(
    //we load the async dependencies
    dependencies_async_list
      .map((dep_elem) =>
        dep_elem.dep_res.value.Widget.map<Updater<FormState>>(
          (value) =>
            (state): FormState => ({
              ...state,
              dependencies_state: state.dependencies_state.set(
                dep_elem.dep_id,
                state.dependencies_state.get(dep_elem.dep_id).set(dep_elem.field_id, { kind: 'async', value })
              )
            })
        )
      )
      .toArray()
      .concat([
        //we load the form
        async(s0.form_data).map<Updater<FormState>>((updater) => (state): FormState => {
          let new_state: FormState = { ...state, form_data: updater(state.form_data) }
          if (new_state.form_data.kind == 'loaded') {
            let all_fields: Immutable.Map<FieldId, FieldData> = Immutable.Map<FieldId, FieldData>()
            new_state.form_data.value.steps.forEach((s) => {
              s.fields.forEach((f) => {
                all_fields = all_fields.set(f.Id, f)
              })
            })
            let dependenciesState: DependenciesState = Immutable.Map()
            new_state.form_data.value.steps.forEach((s) => {
              s.dependencies.forEach((d) => {
                let dependent_fields = Immutable.Map<FieldId, DependencyResult>()
                d.dependent_fields.forEach((f) => {
                  if (all_fields.has(f.FieldId)) {
                    let field_dep_state = dependent_fields.get(f.FieldId)
                    let dependency_result = get_dependency_result(
                      f.FieldId,
                      all_fields.get(f.FieldId).user_input,
                      d.dependency,
                      field_dep_state && field_dep_state.kind == 'async' ? field_dep_state.value : undefined
                    )
                    if (dependency_result.kind == 'simple' && dependency_result.value == false) {
                      dependent_fields = dependent_fields.set(f.FieldId, dependency_result)
                    } else {
                      if (dependency_result.kind == 'async') {
                        if (
                          dependency_result.value.Result.kind == 'loaded' &&
                          dependency_result.value.Result.value == false
                        )
                          dependent_fields = dependent_fields.set(f.FieldId, dependency_result)
                        else {
                          if (dependency_result.value.Result.kind != 'unloaded') {
                            dependent_fields = dependent_fields.set(f.FieldId, dependency_result)
                          }
                        }
                      }
                    }
                  }
                })
                dependenciesState = dependenciesState.set(d.dependency.Id, dependent_fields)
              })
            })
            new_state.dependencies_state = dependenciesState
          }
          return new_state
        }),
        async(s0.form_submission_logic, undefined, {
          key: 'submission form async',
          connectionError: fromJSX(() => <p className="message message--error">{t('form:connection_error')}</p>),
          loading: () => fromJSX(() => <p className="message">{t('form:form_succes')}</p>),
          unauthorizedError: fromJSX(() => <p className="message message--error">{t('form:no_access')}</p>),
          otherError: fromJSX(() => <p className="message message--error">{t('form:error')}</p>),
          unloaded: null
        }).map<Updater<FormState>>((a) => (s) => ({ ...s, form_submission_logic: a(s.form_submission_logic) }))
      ])
  ).wrapHTML((jsx) => (
    <Fabric>
      <Customizer {...FluentCustomizations}>{jsx}</Customizer>
    </Fabric>
  ))
}

export const Form: IOWidget<FormState, Updater<FormState>> = (s0) =>
  any<Updater<FormState>>([
    BaseForm(s0),
    fromJSX((setState) => {
      if (s0.form_data.kind != 'loaded') return <p className="text-1 message">{s0.form_data.kind}</p>
      return form_renderer(
        s0.form_data.value,
        s0,
        (form_submission_logic) => setState((s) => ({ ...s, form_submission_logic })),
        (valueUpdater, dependencies_state): void =>
          setState(
            (state): FormState =>
              state.form_data.kind == 'loaded'
                ? {
                    ...state,
                    form_data: { ...state.form_data, value: valueUpdater(state.form_data.value) },
                    dependencies_state
                  }
                : state
          )
      )
    })
  ]).wrapHTML((jsx) => <div className="paragraph paragraph--form">{jsx}</div>)

export const SuccessForm: IOWidget<FormState, Updater<FormState>> = (s0) =>
  any<Updater<FormState>>([
    BaseForm(s0),
    fromJSX(() => {
      if (s0.form_data.kind != 'loaded') return <p className="text-1 message">{s0.form_data.kind}</p>
      return status_thank_you_renderer(s0.form_data.value.thank_you.content)
    })
  ]).wrapHTML((jsx) => <div className="paragraph paragraph--form">{jsx}</div>)
